// Browser translators (Google Translate, Edge) replace each text node they
// translate with a <font> wrapper. React still holds the original text node,
// so its next removeChild / insertBefore against it targets a node that is no
// longer under that parent, the DOM throws NotFoundError and the page falls
// to the error screen (#350, SMARTMIXIN-WEB-5D/5G/5H; facebook/react#11538).
//
// The guard applies to every such call, translated page or not:
// - removeChild of a node that is not a child: skipped.
// - insertBefore a reference that is not a child: inserted before the
//   reference's nearest ancestor that is, else appended — the new content
//   stays visible, possibly out of order. Skipping it would silently leave a
//   blank area for the rest of the session.
// Both warn; the console warning lands in Sentry breadcrumbs, so a later
// error still shows it. Limit: text React updates after translation may stay
// in the translation.

const GUARDED = Symbol.for('smartmixin.translateDomGuard');

type GuardedNode = typeof Node.prototype & {[GUARDED]?: true};

export const installTranslateDomGuard = (): (() => void) => {
  const proto = Node.prototype as GuardedNode;
  if (proto[GUARDED]) return () => {};

  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;

  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn(
        `translateDomGuard: skipped removeChild of a ${child.nodeName} that is no longer a child of this ${this.nodeName}`,
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  proto.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child && child.parentNode !== this) {
      let anchor: Node | null = child;
      while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
      console.warn(
        `translateDomGuard: reference ${child.nodeName} is no longer a child of this ${this.nodeName}; ${anchor ? 'inserted before its ancestor' : 'appended'}`,
      );
      return originalInsertBefore.call(this, node, anchor) as T;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };

  proto[GUARDED] = true;
  return () => {
    proto.removeChild = originalRemoveChild;
    proto.insertBefore = originalInsertBefore;
    delete proto[GUARDED];
  };
};
